import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { makeApiCall } from '../utils/customerUtils'
import { SearchIcon, MessageIcon } from '../components/Icons'
import { FullWidthContent } from '../components/Layout'
import { useWebSocket } from '../contexts/WebSocketContext'

// Add CSS animation styles
const messageAnimationStyles = `
  @keyframes messageAppear {
    0% {
      opacity: 0;
      transform: translateY(8px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-message-appear {
    animation: messageAppear 0.2s ease-out forwards;
  }
`

// Inject styles into document head
if (typeof document !== 'undefined') {
  const styleElement = document.getElementById('message-animations') || document.createElement('style')
  styleElement.id = 'message-animations'
  styleElement.textContent = messageAnimationStyles
  if (!document.getElementById('message-animations')) {
    document.head.appendChild(styleElement)
  }
}

interface SMSMessage {
  id: number
  content: string
  direction: 'inbound' | 'outbound'
  from_phone: string
  to_phone: string
  created_at: string
  status: string
  customer_id?: number
  person_id?: number
  deal_id?: number
  is_status_update?: boolean
  sent_via_websocket?: boolean // Sent via WebSocket (1 check)
  websocket_confirmed?: boolean // Confirmed delivered via WebSocket (2 checks)
}

interface Conversation {
  contact_id: number
  contact_name: string
  contact_phone: string
  contact_type: 'customer' | 'person' | 'unknown'
  customer_id: number | null
  last_message?: SMSMessage
  unread_count: number
  messages: SMSMessage[]
}

interface Contact {
  id: number
  name: string
  phone: string
  type: 'customer' | 'person'
  customer_id: number
}

interface UserSmsSettings {
  two_way_sms_enabled: boolean
  admin_default_sms_number: string | null
  custom_phone_number?: string | null
}

const Conversations: React.FC = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [newMessage, setNewMessage] = useState('')
  const [sendingMessage, setSendingMessage] = useState(false)
  const [showNewConversationModal, setShowNewConversationModal] = useState(false)
  const [contacts, setContacts] = useState<Contact[]>([])
  const [contactSearch, setContactSearch] = useState('')
  const [phoneNumberInput, setPhoneNumberInput] = useState('')
  const [loadingContacts, setLoadingContacts] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; conversation: Conversation | null }>({ show: false, conversation: null })
  const [deletingConversation, setDeletingConversation] = useState(false)
  const [lastSelectedMessageTime, setLastSelectedMessageTime] = useState<string | null>(null)
  const [newMessageIds, setNewMessageIds] = useState<Set<number>>(new Set())
  const [userSmsSettings, setUserSmsSettings] = useState<UserSmsSettings | null>(null)
  const [loadingSmsSettings, setLoadingSmsSettings] = useState(true)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  
  // WebSocket connection
  const { socket, isConnected } = useWebSocket()
  
  // Track messages sent via WebSocket for checkmark display
  const [websocketMessageIds, setWebsocketMessageIds] = useState<Set<number>>(new Set())
  
  // Use ref to track selected conversation for WebSocket handlers to avoid stale closures
  const selectedConversationRef = useRef<Conversation | null>(null)
  
  // Keep ref in sync with state
  useEffect(() => {
    selectedConversationRef.current = selectedConversation
  }, [selectedConversation])

  useEffect(() => {
    fetchConversations()
    fetchUserSmsSettings()
    
    // Set up polling for new messages - longer interval when WebSocket is connected
    const pollInterval = setInterval(async () => {
      // Only poll if WebSocket is not connected (fallback mode)
      if (!isConnected) {
        const prevConversations = [...conversations]
        await fetchConversations()
        
        // If we have a selected conversation, check if it has new messages
        if (selectedConversation && lastSelectedMessageTime) {
          try {
            const response = await makeApiCall('api/sms/conversations')
            if (response.ok) {
              const data = await response.json()
              const updatedConv = data.conversations?.find((conv: any) => 
                conv.contact_id === selectedConversation.contact_id && 
                conv.contact_type === selectedConversation.contact_type
              )
              
              if (updatedConv?.last_message?.created_at && 
                  new Date(updatedConv.last_message.created_at) > new Date(lastSelectedMessageTime)) {
                console.log('New message detected in active conversation, refreshing...')
                await fetchMessages(selectedConversation)
              }
            }
          } catch (error) {
            console.error('Error checking for new messages:', error)
          }
        }
      }
    }, isConnected ? 30000 : 3000) // Poll every 30s if WebSocket connected, 3s if not

    return () => clearInterval(pollInterval)
  }, [selectedConversation?.contact_id, selectedConversation?.contact_type, conversations.length, lastSelectedMessageTime, isConnected])

  // WebSocket event listeners
  useEffect(() => {
    if (!socket || !isConnected) {
      console.warn('❌ WebSocket not ready:', { hasSocket: !!socket, isConnected })
      return
    }
    
    console.log('🔌 Attaching WebSocket listeners for SMS events')

    // Listen for new SMS messages
    socket.on('sms:new_message', (data) => {
      console.log('📨 New SMS received via WebSocket:', JSON.stringify(data, null, 2))
      
      // Update conversations list
      setConversations(prev => {
        const existing = prev.find(c => 
          c.contact_id === data.conversation.contact_id &&
          c.contact_type === data.conversation.contact_type
        )
        
        // Use ref to get current selected conversation
        const currentSelected = selectedConversationRef.current

        if (existing) {
          // Move to top and update last message
          return [
            { 
              ...existing, 
              last_message: data.message,
              unread_count: currentSelected?.contact_id === existing.contact_id ? 0 : existing.unread_count + 1
            },
            ...prev.filter(c => c.contact_id !== existing.contact_id || c.contact_type !== existing.contact_type)
          ]
        } else {
          // New conversation
          return [{
            contact_id: data.conversation.contact_id,
            contact_name: data.conversation.contact_name,
            contact_phone: data.conversation.contact_phone,
            contact_type: data.conversation.contact_type,
            customer_id: data.conversation.customer_id,
            last_message: data.message,
            unread_count: 1,
            messages: []
          }, ...prev]
        }
      })

      // If viewing this conversation, append message
      const currentSelected = selectedConversationRef.current
      console.log('🔍 Checking if message is for active conversation:', {
        event_contact_id: data.conversation.contact_id,
        event_contact_type: data.conversation.contact_type,
        selected_contact_id: currentSelected?.contact_id,
        selected_contact_type: currentSelected?.contact_type,
        matches: currentSelected?.contact_id === data.conversation.contact_id && 
                currentSelected?.contact_type === data.conversation.contact_type
      })
      
      if (currentSelected?.contact_id === data.conversation.contact_id && 
          currentSelected?.contact_type === data.conversation.contact_type) {
        console.log('✅ Message is for active conversation, appending...')
        setSelectedConversation(prev => {
          if (!prev) return prev
          
          // Check if message already exists
          const messageExists = prev.messages?.some(m => m.id === data.message.id)
          if (messageExists) {
            console.log('⚠️ Message already exists, skipping')
            return prev
          }
          
          console.log('📥 Appending message to conversation:', data.message)
          console.log('Current message count:', prev.messages?.length || 0)
          // Use concat for better performance with large arrays
          const newMessages = (prev.messages || []).concat(data.message)
          console.log('New message count:', newMessages.length)
          
          return {
            ...prev,
            messages: newMessages
          }
        })
        
        // Update last message time to track the latest message
        setLastSelectedMessageTime(data.message.created_at)
        console.log('📊 State updated, new message should be visible')
        
        // Mark new message for animation
        setNewMessageIds(new Set([data.message.id]))
        setTimeout(() => setNewMessageIds(new Set()), 250)
        
        // Scroll to bottom
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' })
          }
        }, 100)
        
        // Mark as read
        if (currentSelected) {
          markMessagesAsRead(currentSelected)
        }
      } else {
        // Show browser notification if not viewing this conversation
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(`New message from ${data.conversation.contact_name}`, {
            body: data.message.content,
            icon: '/logo.png'
          })
        }
      }
    })

    // Listen for sent message confirmation
    socket.on('sms:message_sent', (data) => {
      console.log('✅✅ Message delivery confirmed via WebSocket')
      
      // Mark message as confirmed (2 checks)
      if (data.message?.id) {
        setSelectedConversation(prev => {
          if (!prev) return prev
          
          return {
            ...prev,
            messages: prev.messages.map(msg => 
              msg.id === data.message.id 
                ? { ...msg, websocket_confirmed: true }
                : msg
            )
          }
        })
      }
    })

    // Cleanup listeners
    return () => {
      console.log('🔌 Cleaning up WebSocket listeners')
      socket.off('sms:new_message')
      socket.off('sms:message_sent')
    }
  }, [socket, isConnected]) // Depend on both socket and isConnected to attach listeners when ready

  const fetchUserSmsSettings = async () => {
    try {
      setLoadingSmsSettings(true)
      const response = await makeApiCall('api/settings/sms')
      
      if (response.ok) {
        const data = await response.json()
        setUserSmsSettings({
          two_way_sms_enabled: data.settings?.two_way_sms_enabled || false,
          admin_default_sms_number: data.admin_default_sms_number,
          custom_phone_number: data.settings?.custom_phone_number
        })
      }
    } catch (err) {
      console.error('Error fetching SMS settings:', err)
    } finally {
      setLoadingSmsSettings(false)
    }
  }

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        setError('Authentication required')
        return
      }

      const response = await makeApiCall('api/sms/conversations')

      if (!response.ok) {
        throw new Error('Failed to fetch conversations')
      }

      const data = await response.json()
      setConversations(data.conversations || [])
    } catch (err) {
      console.error('Error fetching conversations:', err)
      setError(err instanceof Error ? err.message : 'Failed to load conversations')
    } finally {
      setLoading(false)
    }
  }

  const fetchMessages = async (conversation: Conversation) => {
    try {
      const response = await makeApiCall(`api/sms/conversations/${conversation.contact_type}/${conversation.contact_id}/messages?limit=500`)
      
      if (response.ok) {
        const data = await response.json()
        // Sort messages by created_at in ascending order (oldest first)
        const messages = (data.messages || []).sort((a: SMSMessage, b: SMSMessage) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        )
        
        // Track new messages for animation
        if (selectedConversation?.messages) {
          const existingIds = new Set(selectedConversation.messages.map(m => m.id))
          const newIds = messages
            .filter((m: SMSMessage) => !existingIds.has(m.id))
            .map((m: SMSMessage) => m.id)
          
          if (newIds.length > 0) {
            setNewMessageIds(new Set(newIds))
            // Clear animation state after animation completes
            setTimeout(() => setNewMessageIds(new Set()), 250)
          }
        }
        
        const updatedConversation = { ...conversation, messages }
        setSelectedConversation(updatedConversation)
        
        // Set the timestamp of the latest message for tracking new messages
        if (messages.length > 0) {
          const latestMessage = messages[messages.length - 1]
          setLastSelectedMessageTime(latestMessage.created_at)
        }
        
        // Update conversations list
        setConversations(prev => prev.map(conv => 
          conv.contact_id === conversation.contact_id && conv.contact_type === conversation.contact_type
            ? { ...conv, unread_count: 0 }
            : conv
        ))

        // Mark messages as read
        markMessagesAsRead(conversation)

        // Scroll to bottom instantly
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ 
              behavior: 'auto',
              block: 'end'
            })
          }
        }, 100)
      }
    } catch (err) {
      console.error('Error fetching messages:', err)
    }
  }

  // Handle URL parameter to auto-open specific conversation from notifications
  useEffect(() => {
    const openCustomerId = searchParams.get('openCustomer')
    if (openCustomerId && conversations.length > 0 && !selectedConversation) {
      const targetConversation = conversations.find(conv => 
        conv.customer_id === parseInt(openCustomerId) || 
        (conv.contact_type === 'customer' && conv.contact_id === parseInt(openCustomerId))
      )
      
      if (targetConversation) {
        // Ensure conversation has messages array before setting as selected
        const conversationWithMessages = {
          ...targetConversation,
          messages: targetConversation.messages || []
        }
        setSelectedConversation(conversationWithMessages)
        setLastSelectedMessageTime(null) // Clear timestamp since we'll fetch fresh messages
        fetchMessages(conversationWithMessages)
        // Clear the URL parameter after opening
        setSearchParams({})
      }
    }
  }, [conversations, searchParams, selectedConversation, setSearchParams, fetchMessages])

  const handleConversationSelect = async (conversation: Conversation) => {
    setSelectedConversation(conversation)
    await fetchMessages(conversation)
    
    // Clear URL params when selecting conversation manually
    if (searchParams.get('contact_type') || searchParams.get('contact_id')) {
      setSearchParams({})
    }
  }

  const handleCloseConversation = () => {
    setSelectedConversation(null)
    setLastSelectedMessageTime(null)
    setSearchParams({})
  }

  const deleteConversation = async (conversation: Conversation) => {
    try {
      setDeletingConversation(true)
      const conversationId = conversation.contact_type === 'unknown' 
        ? encodeURIComponent(conversation.contact_phone)
        : conversation.contact_id

      const response = await makeApiCall(`api/sms/conversations/${conversation.contact_type}/${conversationId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        // Remove from conversations list
        setConversations(prev => prev.filter(conv => 
          !(conv.contact_id === conversation.contact_id && conv.contact_type === conversation.contact_type)
        ))

        // Clear selected conversation if it was the deleted one
        if (selectedConversation?.contact_id === conversation.contact_id && 
            selectedConversation?.contact_type === conversation.contact_type) {
          setSelectedConversation(null)
          setLastSelectedMessageTime(null)
        }

        setDeleteConfirm({ show: false, conversation: null })
      } else {
        throw new Error('Failed to delete conversation')
      }
    } catch (err) {
      console.error('Error deleting conversation:', err)
      alert('Failed to delete conversation. Please try again.')
    } finally {
      setDeletingConversation(false)
    }
  }

  const markRelatedNotificationsAsRead = async (conversation: Conversation) => {
    try {
      // Get all notifications and find SMS notifications related to this conversation
      const response = await makeApiCall('api/notifications?limit=100&unread_only=true')
      if (response.ok) {
        const data = await response.json()
        const relatedNotifications = data.notifications.filter((notif: any) => {
          if (notif.type !== 'sms') return false
          
          // Check if notification is related to this conversation
          if (conversation.customer_id && notif.metadata?.customer_id === conversation.customer_id) {
            return true
          }
          
          if (notif.metadata?.phone && conversation.contact_phone) {
            const cleanNotifPhone = notif.metadata.phone.replace(/[^\d]/g, '')
            const cleanConvPhone = conversation.contact_phone.replace(/[^\d]/g, '')
            return cleanNotifPhone.slice(-10) === cleanConvPhone.slice(-10)
          }
          
          return false
        })

        // Mark each related notification as read
        for (const notification of relatedNotifications) {
          await makeApiCall(`api/notifications/${notification.id}/mark-read`, {
            method: 'POST'
          })
        }

        // Dispatch event to notify NotificationBell to refresh unread count
        if (relatedNotifications.length > 0) {
          const notificationUpdateEvent = new CustomEvent('notificationsMarkedAsRead', {
            detail: { count: relatedNotifications.length }
          })
          window.dispatchEvent(notificationUpdateEvent)
        }
      }
    } catch (err) {
      console.error('Error marking related notifications as read:', err)
    }
  }

  const markMessagesAsRead = async (conversation: Conversation) => {
    try {
      const conversationId = conversation.contact_type === 'unknown' 
        ? encodeURIComponent(conversation.contact_phone)
        : conversation.contact_id

      // Mark SMS messages as read
      await makeApiCall(`api/sms/conversations/${conversation.contact_type}/${conversationId}/mark-read`, {
        method: 'POST'
      })

      // Mark related notifications as read
      await markRelatedNotificationsAsRead(conversation)

      // Update local state to reflect read status
      setConversations(prev => prev.map(conv => 
        conv.contact_id === conversation.contact_id && conv.contact_type === conversation.contact_type
          ? { ...conv, unread_count: 0 }
          : conv
      ))
    } catch (err) {
      console.error('Error marking messages as read:', err)
    }
  }

  const initiateCall = (phoneNumber: string, customerName: string) => {
    // Dispatch the event that the TelnyxCallWidget listens for
    const callEvent = new CustomEvent('triggerTelnyxCall', {
      detail: {
        phoneNumber,
        customerId: selectedConversation?.customer_id,
        customerName
      }
    })
    window.dispatchEvent(callEvent)
    
    // Also dispatch the initiate-call event as fallback
    const fallbackCallEvent = new CustomEvent('initiate-call', {
      detail: {
        phoneNumber,
        customerName,
        customerId: selectedConversation?.customer_id
      }
    })
    window.dispatchEvent(fallbackCallEvent)
  }

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || sendingMessage) return

    try {
      setSendingMessage(true)
      const response = await makeApiCall('api/sms/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: selectedConversation.contact_phone,
          message: newMessage.trim(),
          customer_id: selectedConversation.customer_id,
          person_id: selectedConversation.contact_type === 'person' ? selectedConversation.contact_id : undefined
        })
      })

      if (response.ok) {
        const data = await response.json()
        const dbMessageId = data.dbMessageId
        
        // Mark as sent via WebSocket (will show 1 check) if connected
        if (isConnected && dbMessageId) {
          setWebsocketMessageIds(prev => new Set([...prev, dbMessageId]))
          console.log(`✓ Message ${dbMessageId} sent via WebSocket`)
        }
        
        // Optimistically add the message to the local state immediately
        if (dbMessageId) {
          const optimisticMessage: SMSMessage = {
            id: dbMessageId,
            content: newMessage.trim(),
            direction: 'outbound',
            from_phone: data.from,
            to_phone: selectedConversation.contact_phone,
            created_at: new Date().toISOString(),
            status: 'sent',
            customer_id: selectedConversation.customer_id || undefined,
            is_status_update: false,
            sent_via_websocket: isConnected, // Mark if sent via WebSocket
            websocket_confirmed: false
          }
          
          setSelectedConversation(prev => {
            if (!prev) return prev
            return {
              ...prev,
              messages: [...prev.messages, optimisticMessage]
            }
          })
          
          console.log(`✨ Optimistically added message ${dbMessageId} to local state (WebSocket: ${isConnected})`)
        } else {
          console.warn('⚠️ No dbMessageId, falling back to fetchMessages')
          // Fallback to old behavior if no dbMessageId
          setTimeout(async () => {
            await fetchMessages(selectedConversation)
          }, 150)
        }
        
        setNewMessage('')
        
        // Scroll to bottom to show new message
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' })
          }
        }, 100)
        
        // Refresh conversations list (but don't refetch messages - we already have it)
        fetchConversations()
      } else {
        throw new Error('Failed to send message')
      }
    } catch (err) {
      console.error('Error sending message:', err)
      alert('Failed to send message. Please try again.')
    } finally {
      setSendingMessage(false)
    }
  }

  const fetchContacts = async () => {
    if (loadingContacts) return
    
    try {
      setLoadingContacts(true)
      const response = await makeApiCall(`api/sms/contacts?search=${encodeURIComponent(contactSearch)}`)
      
      if (response.ok) {
        const data = await response.json()
        setContacts(data.contacts || [])
      }
    } catch (err) {
      console.error('Error fetching contacts:', err)
    } finally {
      setLoadingContacts(false)
    }
  }

  const startNewConversation = async (contact: Contact) => {
    // Create a conversation object for the selected contact
    const newConversation: Conversation = {
      contact_id: contact.id,
      contact_name: contact.name,
      contact_phone: contact.phone,
      contact_type: contact.type,
      customer_id: contact.customer_id,
      unread_count: 0,
      messages: []
    }
    
    setSelectedConversation(newConversation)
    setLastSelectedMessageTime(null) // Clear timestamp for new conversation
    setShowNewConversationModal(false)
    setContactSearch('')
    setPhoneNumberInput('')
    setContacts([])
  }

  const startConversationWithPhone = async (phoneNumber: string) => {
    // Create a conversation object for the phone number (unknown contact)
    const newConversation: Conversation = {
      contact_id: 0, // Unknown contact ID
      contact_name: `Unknown (${phoneNumber})`,
      contact_phone: phoneNumber,
      contact_type: 'unknown',
      customer_id: null,
      unread_count: 0,
      messages: []
    }
    
    setSelectedConversation(newConversation)
    setLastSelectedMessageTime(null) // Clear timestamp for new conversation
    setShowNewConversationModal(false)
    setContactSearch('')
    setPhoneNumberInput('')
    setContacts([])
  }

  const openNewConversationModal = () => {
    setShowNewConversationModal(true)
    fetchContacts()
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))
    
    if (diffInDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    } else if (diffInDays === 1) {
      return 'Yesterday'
    } else if (diffInDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' })
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }
  }

  const filteredConversations = conversations.filter(conv => {
    if (!searchTerm) return true
    
    const searchLower = searchTerm.toLowerCase()
    const name = conv.contact_name?.toLowerCase() || ''
    const phone = conv.contact_phone || ''
    
    // Check contact name and phone
    if (name.includes(searchLower) || phone.includes(searchTerm)) {
      return true
    }
    
    // Check last message content
    if (conv.last_message?.content?.toLowerCase().includes(searchLower)) {
      return true
    }
    
    // Check all messages content if available
    if (conv.messages && conv.messages.length > 0) {
      return conv.messages.some(msg => 
        msg.content?.toLowerCase().includes(searchLower)
      )
    }
    
    return false
  })

  // Connection status indicator component
  const ConnectionStatus = () => (
    <div className={`fixed top-20 right-4 px-3 py-2 rounded-lg text-sm shadow-lg z-50 transition-all ${
      isConnected 
        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 border border-green-300 dark:border-green-700' 
        : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 border border-yellow-300 dark:border-yellow-700'
    }`}>
      <div className="flex items-center space-x-2">
        <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'}`} />
        <span className="font-medium">
          {isConnected ? t('conversations.realTime') : t('conversations.reconnecting')}
        </span>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-300">{t('conversations.loadingConversations')}</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-8 text-red-600">
          <span>{error}</span>
        </div>
      </div>
    )
  }

  // Check if user has phone number configured
  const hasPhoneNumber = userSmsSettings?.two_way_sms_enabled && userSmsSettings?.custom_phone_number
  
  if (!loadingSmsSettings && !hasPhoneNumber) {
    return (
      <FullWidthContent className="h-full p-0">
        <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-8">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                {t('conversations.phoneNumberRequired')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('conversations.phoneNumberRequiredDescription')}
              </p>
            </div>
            
            <a
              href="/dashboard/settings/phone-numbers"
              className="inline-flex items-center justify-center px-6 py-3 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {t('conversations.setupPhoneNumber')}
            </a>
            
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                💡 {t('conversations.phoneNumberHint')}
              </p>
            </div>
          </div>
        </div>
      </FullWidthContent>
    )
  }

  return (
    <FullWidthContent className="h-full p-0">
      <ConnectionStatus />
      <div className="h-full flex bg-gray-50 dark:bg-gray-900 overflow-hidden rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      {/* Conversations Sidebar */}
      <div className="w-80 flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
                  {/* Header */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('conversations.title')}</h1>
            <button
              onClick={openNewConversationModal}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('conversations.newChat')}
            </button>
          </div>
          
          {/* Search */}
          <div className="relative">
            <SearchIcon size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('conversations.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                      {filteredConversations.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                {searchTerm ? t('conversations.noConversationsFound') : t('conversations.noConversationsYet')}
              </div>
            ) : (
            filteredConversations.map((conversation) => (
              <div
                key={`${conversation.contact_type}-${conversation.contact_id}`}
                onClick={() => handleConversationSelect(conversation)}
                className={`p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer ${
                  selectedConversation?.contact_id === conversation.contact_id && 
                  selectedConversation?.contact_type === conversation.contact_type
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500'
                    : ''
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                      <span className="text-gray-900 dark:text-gray-600 font-bold text-sm">
                        {conversation.contact_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {conversation.contact_name}
                        </h3>
                        {conversation.contact_type === 'person' && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                            {t('conversations.relatedPerson')}
                          </span>
                        )}
                        {conversation.contact_type === 'unknown' && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400">
                            {t('conversations.unknown')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{conversation.contact_phone}</p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    {conversation.last_message && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {formatTime(conversation.last_message.created_at)}
                      </div>
                    )}
                    {conversation.unread_count > 0 && (
                      <div className="w-5 h-5 bg-gray-800 rounded-full flex items-center justify-center">
                        <span className="text-xs text-white font-medium">
                          {conversation.unread_count > 9 ? '9+' : conversation.unread_count}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                {conversation.last_message && (
                  <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
                    {conversation.last_message.content}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 bg-gray-50 dark:bg-gray-900">
        {selectedConversation ? (
          <div className="h-full flex flex-col">
            {/* Chat Header */}
            <div className="p-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <span className="text-gray-900 dark:text-gray-600 font-bold text-sm">
                    {selectedConversation.contact_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </span>
                </div>
                <div>
                  <h2 className="font-medium text-gray-900 dark:text-white">
                    {selectedConversation.contact_name}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedConversation.contact_phone}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => initiateCall(selectedConversation.contact_phone, selectedConversation.contact_name)}
                  className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Call this contact"
                >
                  <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                  </svg>
                </button>
                <button 
                  onClick={() => setDeleteConfirm({ show: true, conversation: selectedConversation })}
                  className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Delete conversation"
                >
                  <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: 'calc(100vh - 280px)' }}>
              {!selectedConversation.messages || selectedConversation.messages.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                  <p>Loading messages...</p>
                </div>
              ) : (
                selectedConversation.messages.map((message, index) => {
                  const isNewMessage = newMessageIds.has(message.id)
                  const isSending = sendingMessage && message.direction === 'outbound' && 
                    selectedConversation.messages[selectedConversation.messages.length - 1]?.id === message.id
                  
                  // Calculate animation delay for staggered effect when multiple new messages
                  const newMessagesList = selectedConversation.messages.filter(m => newMessageIds.has(m.id))
                  const newMessageIndex = newMessagesList.findIndex(m => m.id === message.id)
                  const animationDelay = isNewMessage ? newMessageIndex * 30 : 0
                  
                  return (
                    <div
                      key={message.id}
                      className={`flex ${message.direction === 'outbound' ? 'justify-end' : 'justify-start'} ${
                        isNewMessage ? 'animate-message-appear' : ''
                      }`}
                      style={{
                        animationDelay: isNewMessage ? `${animationDelay}ms` : undefined
                      }}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl transition-all duration-200 ${
                          message.direction === 'outbound'
                            ? `bg-gray-800 text-white ${isSending ? 'opacity-70 scale-95' : ''}`
                            : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700'
                        }`}
                      >
                    <p className="text-sm">{message.content}</p>
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center space-x-2">
                        <p className={`text-xs ${
                          message.direction === 'outbound' 
                            ? 'text-blue-100' 
                            : 'text-gray-500 dark:text-gray-400'
                        }`}>
                          {formatTime(message.created_at)}
                        </p>
                        {/* WhatsApp-style checkmarks - only for WebSocket-sent messages */}
                        {message.direction === 'outbound' && (message.sent_via_websocket || websocketMessageIds.has(message.id)) && (
                          <div className="flex items-center">
                            {message.websocket_confirmed ? (
                              // Double check - message confirmed delivered
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-blue-100">
                                <path d="M2 8.5L5.5 12L14 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M5 8.5L8.5 12L17 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            ) : (
                              // Single check - message sent
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-blue-100">
                                <path d="M3 8.5L7 12.5L13 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                        )}
                      </div>
                      {message.is_status_update && (
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          message.direction === 'outbound'
                            ? 'bg-blue-400 text-blue-100'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}>
                          Auto
                        </span>
                      )}
                    </div>
                  </div>
                    </div>
                  )
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 relative">
              {/* Two-way SMS disabled overlay */}
              {userSmsSettings && !userSmsSettings.two_way_sms_enabled && (
                <div className="absolute inset-0 bg-gray-500/50 backdrop-blur-sm flex items-center justify-center z-10">
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-w-md text-center">
                    <div className="mb-4">
                      <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
                        <MessageIcon size={24} className="text-gray-900 dark:text-gray-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        Two-Way SMS Required
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                        To send personalized messages, you need to activate two-way SMS in your settings.
                        {userSmsSettings.admin_default_sms_number && (
                          <span className="block mt-2">
                            Automatic status updates are still sent from: <br/>
                            <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">
                              {userSmsSettings.admin_default_sms_number}
                            </code>
                          </span>
                        )}
                      </p>
                    </div>
                    <a
                      href="/dashboard/settings/sms"
                      className="inline-flex items-center px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
                    >
                      Activate Two-Way SMS
                    </a>
                  </div>
                </div>
              )}
              
              <div className="flex items-center space-x-3">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !loadingSmsSettings && userSmsSettings?.two_way_sms_enabled && sendMessage()}
                  placeholder={loadingSmsSettings ? "Loading..." : userSmsSettings?.two_way_sms_enabled ? "Type a message..." : "Two-way SMS disabled"}
                  className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
                  disabled={sendingMessage || loadingSmsSettings || !userSmsSettings?.two_way_sms_enabled}
                />
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || sendingMessage || loadingSmsSettings || !userSmsSettings?.two_way_sms_enabled}
                  className="p-2 bg-gray-800 text-white rounded-full hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {sendingMessage ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <MessageIcon size={20} />
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <MessageIcon size={64} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('conversations.selectConversation')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {t('conversations.selectConversationDescription')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* New Conversation Modal */}
      {showNewConversationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {t('conversations.startNewConversation')}
                </h3>
                <button
                  onClick={() => {
                    setShowNewConversationModal(false)
                    setContactSearch('')
                    setPhoneNumberInput('')
                    setContacts([])
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <input
                  type="text"
                  placeholder={t('conversations.searchContactsPlaceholder')}
                  value={contactSearch}
                  onChange={(e) => {
                    setContactSearch(e.target.value)
                    if (e.target.value.trim()) {
                      fetchContacts()
                    } else {
                      setContacts([])
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>

              <div className="mb-4">
                <div className="flex items-center mb-2">
                  <div className="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
                  <span className="px-3 text-sm text-gray-500 dark:text-gray-400">{t('conversations.orEnterPhoneNumber')}</span>
                  <div className="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
                </div>
                <div className="flex space-x-2">
                  <input
                    type="tel"
                    placeholder={t('conversations.phoneNumberPlaceholder')}
                    value={phoneNumberInput}
                    onChange={(e) => setPhoneNumberInput(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                  <button
                    onClick={() => {
                      if (phoneNumberInput.trim()) {
                        // Validate phone number has at least 10 digits
                        const digitsOnly = phoneNumberInput.replace(/\D/g, '')
                        if (digitsOnly.length >= 10) {
                          startConversationWithPhone(phoneNumberInput.trim())
                        } else {
                          alert(t('conversations.invalidPhoneNumber'))
                        }
                      }
                    }}
                    disabled={!phoneNumberInput.trim()}
                    className="px-4 py-2 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white rounded-lg transition-colors disabled:cursor-not-allowed"
                  >
                    {t('conversations.startConversation')}
                  </button>
                </div>
              </div>

              <div className="max-h-60 overflow-y-auto">
                {loadingContacts ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                    <span className="ml-2 text-gray-600 dark:text-gray-300">{t('conversations.searching')}</span>
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    {contactSearch ? t('conversations.noContactsFound') : t('conversations.typeToSearch')}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {contacts.map((contact) => (
                      <button
                        key={`${contact.type}-${contact.id}`}
                        onClick={() => startNewConversation(contact)}
                        className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                            <span className="text-gray-900 dark:text-gray-600 font-bold text-sm">
                              {contact.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                            </span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h4 className="font-medium text-gray-900 dark:text-white">
                                {contact.name}
                              </h4>
                              {contact.type === 'person' && (
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                                  {t('conversations.contact')}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {contact.phone}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && deleteConfirm.conversation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {t('conversations.deleteConversation')}
                </h3>
                <button
                  onClick={() => setDeleteConfirm({ show: false, conversation: null })}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  disabled={deletingConversation}
                >
                  <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-600 dark:text-gray-300 mb-2">
                  {t('conversations.deleteConversationConfirm')}{' '}
                  <span className="font-medium text-gray-900 dark:text-white">
                    {deleteConfirm.conversation.contact_name}
                  </span>?
                </p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  {t('conversations.deleteConversationWarning')}
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeleteConfirm({ show: false, conversation: null })}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                  disabled={deletingConversation}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => deleteConversation(deleteConfirm.conversation!)}
                  disabled={deletingConversation}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {deletingConversation ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </FullWidthContent>
  )
}

export default Conversations 